import React, { useState, useEffect, useRef } from "react"
import { HeartIcon as HeartIconOutline } from "@heroicons/react/24/outline"
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import FingerprintJS from "@fingerprintjs/fingerprintjs"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Reactions from "aldoo-ra/Reactions"
import PubSub from "pubsub-js"

const reactions = Reactions.getInstance()

const PageLike = ({ target, variant = "like_btn", label, className = "" }) => {
  const [likes, setLikes] = useState(0)
  const [hasLiked, setHasLiked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLikeAnimating, setIsLikeAnimating] = useState(false)
  const [auth] = PlatformAuth.useAuth()
  const isMounted = useRef(true)

  // Create a unique topic for this content
  const likeTopic = target?._id ? `likes-${target._id}` : null

  // Setup mount/unmount handling
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  // Handle content changes and subscription
  useEffect(() => {
    if (!target?._id) {
      setIsLoading(false)
      return
    }

    let token
    if (likeTopic) {
      token = PubSub.subscribe(likeTopic, (topic, data) => {
        if (!isMounted.current) return

        // Only update if the data matches our target
        if (data.contentId === target._id) {
          setLikes(data.likes)
          setHasLiked(data.hasLiked)
        }
      })
    }

    // Initial like status check
    checkLikeStatus()

    // Cleanup subscription
    return () => {
      if (token) {
        PubSub.unsubscribe(token)
      }
    }
  }, [target?._id])

  const checkLikeStatus = async () => {
    if (!target?._id || !isMounted.current) return

    setIsLoading(true)

    try {
      const identifier = await getIdentifier()
      const contentId = target._id

      const result = await reactions.getStatus({
        contentId,
        identifier,
        isAnonymous: !auth,
        reaction: "like",
      })

      if (!isMounted.current) return

      // Verify this response matches our target
      if (result.contentId && result.contentId !== contentId) {
        setIsLoading(false)
        return
      }

      if (result.error) {
        setIsLoading(false)
        return
      }

      // Parse the result to determine like status and count
      let userHasLiked = false
      let likeCount = 0

      if (result.hasReaction !== undefined) {
        userHasLiked = !!result.hasReaction
        likeCount = result.count || 0
      } else if (result.reaction) {
        if (Array.isArray(result.reaction)) {
          userHasLiked = result.reaction.includes("like")
        } else {
          userHasLiked = result.reaction === "like"
        }
        likeCount = (result.counts && result.counts.like) || 0
      } else if (result.counts) {
        userHasLiked = "like" in result.counts
        likeCount = result.counts.like || 0
      }

      if (isMounted.current) {
        setHasLiked(userHasLiked)
        setLikes(likeCount)
        setIsLoading(false)
      }
    } catch (error) {
      if (isMounted.current) {
        setHasLiked(false)
        setLikes(0)
        setIsLoading(false)
      }
    }
  }

  const handleLike = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (isLoading || !target?._id || !isMounted.current) return

    const previousState = {
      likes,
      hasLiked,
    }

    const contentId = target._id

    // Trigger animation
    setIsLikeAnimating(true)
    // Animation will be cleared on next render, no need for timeout cleanup

    // Optimistically update UI
    const newHasLiked = !hasLiked
    setHasLiked(newHasLiked)
    setLikes((prev) => prev + (hasLiked ? -1 : 1))

    try {
      const identifier = await getIdentifier()
      let response

      response = await reactions.toggleReaction({
        contentId,
        identifier,
        isAnonymous: !auth,
        reaction: "like",
      })

      if (!isMounted.current) return

      if (response.error) {
        throw new Error(response.error)
      }

      // Determine the like count from response
      let likeCount = 0

      if (response.count !== undefined) {
        likeCount = response.count
      } else if (
        response.counts &&
        typeof response.counts.like !== "undefined"
      ) {
        likeCount = response.counts.like
      } else {
        // Get count if not available in response
        const countResult = await reactions.getStatus({
          contentId,
          identifier,
          isAnonymous: !auth,
          reaction: "like",
        })

        if (!isMounted.current) return

        if (countResult.count !== undefined) {
          likeCount = countResult.count
        } else if (
          countResult.counts &&
          countResult.counts.like !== undefined
        ) {
          likeCount = countResult.counts.like
        }
      }

      const currentHasLiked = !hasLiked

      // Publish update to all instances
      if (likeTopic) {
        PubSub.publish(likeTopic, {
          contentId,
          likes: likeCount,
          hasLiked: currentHasLiked,
        })
      }

      // Update local state
      if (isMounted.current) {
        setLikes(likeCount)
        setHasLiked(currentHasLiked)
      }
    } catch (error) {
      if (isMounted.current) {
        setLikes(previousState.likes)
        setHasLiked(previousState.hasLiked)
        showNotification({
          message: "Failed to update like",
          className: "bg-error text-white",
        })
      }
    } finally {
      if (isMounted.current) {
        setIsLikeAnimating(false)
      }
    }
  }

  const getIdentifier = async () => {
    if (auth?._id) {
      return auth._id
    }
    if (!window.fpPromise) {
      window.fpPromise = FingerprintJS.load()
    }
    const fp = await window.fpPromise
    const result = await fp.get()
    return result.visitorId
  }

  return (
    <Button
      onClick={handleLike}
      disabled={isLoading}
      variant={variant}
      className={className}
    >
      {label && <span className="mr-2">{label}</span>}

      {hasLiked ? (
        <HeartIconSolid
          className={`
            w-3 md:w-4 h-3 md:h-4 text-primary
            ${isLikeAnimating ? "scale-125" : "hover:scale-110"}
            transition-transform duration-200 ease-in-out
            mr-1
          `}
        />
      ) : (
        <HeartIconOutline
          className={`
            w-3 md:w-4 h-3 md:h-4 text-text_secondary
            ${isLikeAnimating ? "scale-125" : "hover:scale-110"}
            transition-transform duration-500 ease-in-out
            mr-1
          `}
        />
      )}

      <Typography
        variant="txt1_dim"
        className="transition-all duration-500"
      >
        {likes}
      </Typography>
    </Button>
  )
}

export default PageLike
